import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Pets as PetsIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { animalService, Animal } from '../../services/animalService';

interface AnimalDetailProprietaireProps {
  animalId: number;
  onBack: () => void;
  onEdit?: (animal: Animal) => void;
}

export const AnimalDetailProprietaire: React.FC<AnimalDetailProprietaireProps> = ({
  animalId,
  onBack,
  onEdit,
}) => {
  const [animal, setAnimal] = useState<Animal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchAnimal = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await animalService.getById(animalId);
        setAnimal(data);
      } catch (err: any) {
        console.error('Erreur lors du chargement de l\'animal:', err);
        setError('Erreur lors du chargement des données de l\'animal');
      } finally {
        setLoading(false);
      }
    };

    fetchAnimal();
  }, [animalId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const calculateAge = (dateNaissance: string) => {
    if (!dateNaissance) return 'Âge inconnu';
    const birthDate = new Date(dateNaissance);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age === 0) {
      const months = today.getMonth() - birthDate.getMonth() +
        (12 * (today.getFullYear() - birthDate.getFullYear()));
      return months > 0 ? `${months} mois` : 'Nouveau-né';
    }
    return `${age} an${age > 1 ? 's' : ''}`;
  };

  const getSpecieColor = (espece: string) => {
    const colors: Record<string, string> = {
      'Chien': '#FF9800',
      'Chat': '#9C27B0',
      'Lapin': '#4CAF50',
      'Oiseau': '#2196F3',
      'Poisson': '#00BCD4',
      'Reptile': '#795548',
      'Hamster': '#FF5722',
      'Furet': '#607D8B',
      'Cochon d\'Inde': '#FFC107',
    };
    return colors[espece] || '#757575';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !animal) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Animal non trouvé'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={onBack} aria-label="retour">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Fiche de {animal.nom}
          </Typography>
        </Box>
        {onEdit && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => onEdit(animal)}
          >
            Modifier
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Avatar
            src={animal.photo_url}
            sx={{
              width: 120,
              height: 120,
              bgcolor: getSpecieColor(animal.espece),
              fontSize: '3rem',
            }}
          >
            {animal.photo_url ? null : <PetsIcon fontSize="large" />}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h5" gutterBottom>
                {animal.nom}
              </Typography>
              <Chip
                label={animal.espece}
                size="small"
                sx={{
                  bgcolor: getSpecieColor(animal.espece),
                  color: 'white',
                }}
              />
              <Chip
                label={animal.sexe === 'M' ? 'Mâle' : 'Femelle'}
                size="small"
                color={animal.sexe === 'M' ? 'primary' : 'secondary'}
                variant="outlined"
              />
              {animal.sterilise && (
                <Chip label="Stérilisé(e)" size="small" color="success" variant="outlined" />
              )}
            </Box>

            <Typography variant="body1" color="text.secondary" gutterBottom>
              {animal.race || 'Race non spécifiée'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
              {animal.date_naissance && (
                <Typography variant="body2">
                  <strong>Date de naissance :</strong> {formatDate(animal.date_naissance)} ({calculateAge(animal.date_naissance)})
                </Typography>
              )}
              {animal.poids != null && (
                <Typography variant="body2">
                  <strong>Poids :</strong> {animal.poids} kg
                </Typography>
              )}
              {animal.numero_puce && (
                <Typography variant="body2">
                  <strong>Numéro de puce :</strong> {animal.numero_puce}
                </Typography>
              )}
              {animal.numero_tatouage && (
                <Typography variant="body2">
                  <strong>Numéro de tatouage :</strong> {animal.numero_tatouage}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
